import React, { useEffect, useState } from 'react';
import { Award, ChevronRight } from 'lucide-react';
import { VendorBadgeAward } from '../types';
import { vendorApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';
import VendorBadgeIcon from './VendorBadgeIcon';

interface VendorBadgeDisplayProps {
  vendorId: number;
  compact?: boolean; // Show fewer badges in compact mode
}

/**
 * Display earned badges on vendor profile pages (Issue #684, #955)
 * Shows grid layout of badge icons with tooltips
 */
export default function VendorBadgeDisplay({
  vendorId,
  compact = false,
}: VendorBadgeDisplayProps) {
  const [badges, setBadges] = useState<VendorBadgeAward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await vendorApi.getVendorBadges(vendorId);
        // Sort by awarded_at (most recent first)
        const sorted = data.sort(
          (a, b) =>
            new Date(b.awarded_at).getTime() - new Date(a.awarded_at).getTime()
        );
        setBadges(sorted);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load vendor badges:', apiError);
        setError(apiError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBadges();
  }, [vendorId]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-4"
        data-testid="badge-display-loading"
      >
        <div className="animate-pulse flex space-x-2">
          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="text-sm text-red-600 py-2"
        data-testid="badge-display-error"
      >
        Failed to load badges: {error}
      </div>
    );
  }

  if (badges.length === 0) {
    return compact ? null : (
      <div
        className="text-sm text-gray-500 py-4 flex items-center gap-2"
        data-testid="badge-display-empty"
      >
        <Award className="w-4 h-4" />
        <span>No badges earned yet</span>
      </div>
    );
  }

  // In compact mode, show first 3 badges when collapsed (prioritize verified badge)
  // When expanded, show all badges in both compact and full modes
  const displayBadges = compact
    ? expanded
      ? badges // Compact + expanded: show all badges
      : (() => {
          // Compact + collapsed: show first 3, prioritize verified
          const verifiedBadge = badges.find(
            (award) => award.badge?.badge_type === 'verification'
          );
          const otherBadges = badges.filter(
            (award) => award.badge?.badge_type !== 'verification'
          );
          const selected = verifiedBadge
            ? [verifiedBadge, ...otherBadges.slice(0, 2)]
            : otherBadges.slice(0, 3);
          return selected;
        })()
    : expanded
      ? badges // Full + expanded: show all badges
      : badges.slice(0, 6); // Full + collapsed: show first 6

  const hasMore = badges.length > displayBadges.length;

  return (
    <div className="space-y-2" data-testid="vendor-badge-display">
      {/* Badge Grid */}
      <div
        className={`
        flex ${compact ? 'gap-2' : 'gap-3'} flex-wrap
        ${compact ? 'items-center' : 'items-start'}
      `}
      >
        {displayBadges.map((award) =>
          award.badge ? (
            <div key={award.id} className="relative group">
              <VendorBadgeIcon
                badge={award.badge}
                earned={true}
                size={compact ? 'sm' : 'md'}
                showTooltip={true}
              />
              {/* Expiration indicator */}
              {award.expires_at && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white">
                  <span className="sr-only">
                    Expires {new Date(award.expires_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          ) : null
        )}

        {/* Show more button in compact mode */}
        {compact && hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center text-sm text-secondary hover:text-primary-dark font-medium"
            data-testid="badge-display-show-more"
          >
            +{badges.length - displayBadges.length}
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        )}
      </div>

      {/* Expand/collapse button in full mode */}
      {!compact && hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-secondary hover:text-primary-dark font-medium flex items-center gap-1"
          data-testid="badge-display-toggle"
        >
          {expanded ? 'Show less' : `Show all ${badges.length} badges`}
          <ChevronRight
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>
      )}
    </div>
  );
}
